import type { AvailableUnits } from '@cw-elements/host-kit/topbar-navigation/topbar-navigation.model';
import { SetUnitSystem } from '@protector/entities/core/__legacy__/redux.actions';
import { setCurrentUnitSystem } from '@protector/utils/l10n';
import { getCurrentUnitSystem as getCurrentUnitSystemFunc } from 'core/utils/functions';
import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import { getSelectedCompany } from 'entities/company/company.selectors';
import { tryCatchWithMonitoring } from 'infra/monitoring/monitoring.utils';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const UNIT_SYSTEM_CHANGED_BY_USER_KEY = 'unitSystemChangedByUser';

type UnitSystemChangedByUserMap = Record<string, AvailableUnits | boolean>;

const getUnitSystemChangedByUserMap = (): UnitSystemChangedByUserMap => {
  const raw = localStorage.getItem(UNIT_SYSTEM_CHANGED_BY_USER_KEY);

  if (!raw) return {};

  try {
    const parsed: unknown = JSON.parse(raw);

    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed as UnitSystemChangedByUserMap;
    }

    return {};
  } catch {
    return {};
  }
};

const useUnitSystem = () => {
  const segmentTracking = useSegmentTracking();

  const selectedCompany = useSelector(getSelectedCompany);

  const dispatch = useDispatch();

  const handleCurrentUnitSystem = useCallback(
    (key: AvailableUnits) => {
      if (!selectedCompany) return;

      dispatch(SetUnitSystem(key, segmentTracking));
      const changedByUserMap = getUnitSystemChangedByUserMap();
      changedByUserMap[selectedCompany.id] = key;
      localStorage.setItem(UNIT_SYSTEM_CHANGED_BY_USER_KEY, JSON.stringify(changedByUserMap));
      setCurrentUnitSystem(key);
    },
    [dispatch, segmentTracking, selectedCompany]
  );

  useEffect(() => {
    if (!selectedCompany) return;

    let userPreference: AvailableUnits | boolean | undefined;

    void tryCatchWithMonitoring(
      () => {
        const changedByUserMap = getUnitSystemChangedByUserMap();
        userPreference = changedByUserMap[selectedCompany.id];
      },
      {
        domain: 'CWElementsWrappers',
        errorType: 'JSONParseError'
      },
      () => {
        userPreference = undefined;
      }
    );

    const newUnitSystem =
      typeof userPreference === 'string' ? userPreference : (selectedCompany.unit_system.toLowerCase() as Lowercase<AvailableUnits>);

    if (newUnitSystem) {
      const currentUnitSystem = getCurrentUnitSystemFunc();

      if (newUnitSystem !== currentUnitSystem) {
        dispatch(SetUnitSystem(newUnitSystem));
        setCurrentUnitSystem(newUnitSystem);
      }
    }
  }, [selectedCompany, dispatch]);

  return useMemo(
    () => ({
      handleCurrentUnitSystem
    }),
    [handleCurrentUnitSystem]
  );
};

export { useUnitSystem };
